import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useAuth} from '../../context/AuthContext';
import {useNavigate, useParams} from 'react-router-dom';
import Arrow from '../../assets/icons/Left.svg';
import apiClient from '../../context/apiClient';
import dayjs from 'dayjs';
import EventLeistungspaketeCard from '../Leistungspakete/EventLeistungspaketeCard';
import ExcelExportIcon from '../../assets/icons/Excel_Export.svg';
import useRules from '../../hooks/useRules';

import Coffee from '../../assets/icons/Clipboard.svg';
import Users from '../../assets/icons/Users.svg';
import Tool from '../../assets/icons/Tool.svg';

const roleIcons = {
    instruktoren: Users,
    mechaniker: Tool,
    guestmanager: Coffee
};

const eventTypeLabels = {
    ADEC: 'ADEC',
    GROS_DOLLN: 'Groß Dölln',
    TOUREN: 'Touren',
    ASCE: 'ASCE',
    FINNLAND: 'Finnland',
    SEEFELD: 'Seefeld',
    SAALBACH: 'Saalbach',
    ADE: 'ADE',
    NICHT_EVENT: 'Nicht-Eventeinsätze'
};

const paketOrder = [
    'FS5', 'FS5HT', 'FSTA', 'FSA', 'FSTaxi', 'FSTour', 'FSK', 'FSKHT', 'FSMod', 'FSReise',
    'TB', 'TBHT', 'TBA', 'TBReise', 'TBReiseH',
    'FZ6', 'FZ6HT', 'FZ6A', 'FZ6AHT', 'FZ6AVorNach', 'FZ6AHTVorNach', 'FZ6AReise', 'FZ6AReiseH'
];

const LeistungspaketeStandort = () => {
    const {user} = useAuth();
    const navigate = useNavigate();
    const {eventType} = useParams();
    const { paketPreise, excludedSet } = useRules();

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [anzeigeModus, setAnzeigeModus] = useState('anzahl');
    const [searchParams] = useSearchParams();
    const fromParam = searchParams.get('from');
    const toParam = searchParams.get('to');

    const [from, setFrom] = useState(() =>
        fromParam || dayjs().startOf('month').format('YYYY-MM-DD')
    );
    const [to, setTo] = useState(() =>
        toParam || dayjs().endOf('month').format('YYYY-MM-DD')
    );
    const [extraPaketCounts, setExtraPaketCounts] = useState({});
    const [nichtEventStundenkontingente, setNichtEventStundenkontingente] = useState([]);
    const [rotePaketeMap, setRotePaketeMap] = useState({});


    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    useEffect(() => {
        if (user && eventType && from && to) {
            fetchEvents();
            fetchStundenkontingenteExtra();
            fetchNichtEventStundenkontingente()

        }
    }, [user, eventType]);


    const fetchEvents = async () => {
        setLoading(true);
        try {
            const formattedFrom = dayjs(from).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
            const formattedTo = dayjs(to).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
            const response = await apiClient.get('/api/events/getLeistungspaketeStandort', {
                params: {from: formattedFrom, to: formattedTo, eventType},
                withCredentials: true,
            });
            setEvents(response.data);

            const redPackages = {};

            response.data.forEach(event => {
                const allPeople = [...(event.employeeList || []), ...(event.leader ? [event.leader] : [])];

                allPeople.forEach(person => {
                    const fullName = `${person.firstName}, ${person.lastName}`;
                    if (!excludedSet.has(fullName)) return;

                    const role = person.role || 'LEADER';
                    const pakete = event.leistungspakete?.[person.id] || [];

                    if (!redPackages[role]) redPackages[role] = [];
                    redPackages[role].push(...pakete);
                });
            });

            setRotePaketeMap(redPackages);

        } catch (err) {
            console.error('Fehler beim Laden:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStundenkontingenteExtra = async () => {
        try {
            const response = await apiClient.get('/api/employee/getStundenkontingenteVorNachFromTo', {
                params: {
                    startTime: `${from}T00:00:00`,
                    endTime: `${to}T23:59:59`,
                    type: eventType,
                },
                withCredentials: true,
            });

            const data = response.data || [];

            const newExtras = {
                instruktoren: {},
                mechaniker: {},
                guestmanager: {},
            };

            if (data[0] > 0 || data[1] > 0) {
                const full = data[0];
                const half = data[1];
                newExtras.instruktoren['FS5VorNach'] = (newExtras.instruktoren['FS5VorNach'] || 0) + full;
                newExtras.instruktoren['FS5HTVorNach'] = (newExtras.instruktoren['FS5HTVorNach'] || 0) + half;
            }

            // Mechaniker
            if (data[2] > 0 || data[3] > 0) {
                const full = data[2];
                const half = data[3];
                newExtras.mechaniker['FZ6AVorNach'] = (newExtras.mechaniker['FZ6AVorNach'] || 0) + full;
                newExtras.mechaniker['FZ6AHTVorNach'] = (newExtras.mechaniker['FZ6AHTVorNach'] || 0) + half;
            }

            // Guestmanager
            if (data[4] > 0 || data[5] > 0) {
                const full = data[4];
                const half = data[5];
                newExtras.guestmanager['TBVorNach'] = (newExtras.guestmanager['TBVorNach'] || 0) + full;
                newExtras.guestmanager['TBHTVorNach'] = (newExtras.guestmanager['TBHTVorNach'] || 0) + half;
            }

            setExtraPaketCounts(newExtras);
        } catch (err) {
            console.error('Fehler beim Laden der Extra-Kontingente:', err);
        }
    };

    const handleRotePaketeUpdate = (role, pakete) => {
        setRotePaketeMap(prev => ({
            ...prev,
            [role]: [...(prev[role] || []), ...pakete],
        }));
    };

    useEffect(() => {
        console.log("🟥 Collected red packages:", rotePaketeMap);
    }, [rotePaketeMap]);


    const fetchNichtEventStundenkontingente = async () => {
        try {
            const response = await apiClient.get('/api/employee/getStundenkontingenteByTimeAndCategory', {
                params: {
                    startTime: `${from}T00:00:00`,
                    endTime: `${to}T23:59:59`,
                    category: 'NICHT_EVENT',
                },
                withCredentials: true,
            });

            const totalHours = response.data || 0;

            // Calculate FSK (10 hours) and FSKHT (5 hours) packages
            const fskCount = Math.floor(totalHours / 10);
            const remainingHours = totalHours % 10;
            const fskhtCount = Math.ceil(remainingHours / 5);

            // Add these packages to your instruktoren counter
            const nichtEventPakete = {};
            if (fskCount > 0) nichtEventPakete['FSK'] = fskCount;
            if (fskhtCount > 0) nichtEventPakete['FSKHT'] = fskhtCount;

            setNichtEventStundenkontingente(nichtEventPakete);

            console.log('Nicht-Event calculation:', {
                totalHours,
                fskCount,
                fskhtCount,
                packages: nichtEventPakete
            });

        } catch (err) {
            console.error('Fehler beim Laden der Nicht-Event-Stundenkontingente:', err);
        }
    }

    const handleExportExcel = async () => {
        const formattedFrom = dayjs(from).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const formattedTo = dayjs(to).endOf('day').format('YYYY-MM-DDTHH:mm:ss');

        try {
            const response = await apiClient.get('/api/events/getLPExcelExportFromTo', {
                params: {
                    from: formattedFrom,
                    to: formattedTo,
                    eventType: eventType,
                },
                responseType: 'blob',
                withCredentials: true,
            });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            const formattedFromDate = dayjs(from).format('YYYY-MM-DD');
            const formattedToDate = dayjs(to).format('YYYY-MM-DD');
            link.download = `Leistungspakete_${eventType}_${formattedFromDate}_bis_${formattedToDate}.xlsx`;
            link.click();
        } catch (error) {
            console.error('Excel Export fehlgeschlagen:', error);
        }
    };

    const paketCounterInstruktoren = {};
    const paketCounterMechaniker = {};
    const paketCounterGuestmanager = {};

    events.forEach(event => {
        const allRoles = [...(event.employeeList || []), ...(event.leader ? [event.leader] : [])];
        allRoles.forEach(person => {
            const fullName = `${person.firstName}, ${person.lastName}`;
            if (excludedSet.has(fullName)) return;

            const pakete = event.leistungspakete?.[person.id] || [];
            const counter = person.role === 'MECHANIC'
                ? paketCounterMechaniker
                : person.role === 'GUEST_MANAGER'
                    ? paketCounterGuestmanager
                    : paketCounterInstruktoren;
            pakete.forEach(p => counter[p] = (counter[p] || 0) + 1);
        });
    });

    Object.entries(extraPaketCounts.instruktoren || {}).forEach(([paket, count]) => {
        paketCounterInstruktoren[paket] = (paketCounterInstruktoren[paket] || 0) + count;
    });
    Object.entries(extraPaketCounts.mechaniker || {}).forEach(([paket, count]) => {
        paketCounterMechaniker[paket] = (paketCounterMechaniker[paket] || 0) + count;
    });
    Object.entries(extraPaketCounts.guestmanager || {}).forEach(([paket, count]) => {
        paketCounterGuestmanager[paket] = (paketCounterGuestmanager[paket] || 0) + count;
    });
    if (eventType === 'NICHT_EVENT') {
        Object.entries(nichtEventStundenkontingente).forEach(([paket, count]) => {
            paketCounterInstruktoren[paket] = (paketCounterInstruktoren[paket] || 0) + count;
        });
    }

    const excludedExtraKeys = [
        'FS5VorNach', 'FS5VorNachHalle', 'FS5HTVorNach', 'FS5HTVorNachHalle',
        'FZ6AVorNachHalle',
        'TBVorNach', 'TBVorNachHalle', 'TBHTVorNach', 'TBHTVorNachHalle'
    ];

    const roleKeyToApiRole = {
        instruktoren: "INSTRUCTOR",
        mechaniker: "MECHANIC",
        guestmanager: "GUEST_MANAGER"
    };


    const calcSum = (counter, role) => {
        return Object.entries(counter).reduce((sum, [paket, count]) => {
            const isExcluded =
                excludedExtraKeys.includes(paket) &&
                extraPaketCounts?.[role]?.[paket];

            if (isExcluded) {
                // Only exclude the extra portion from sum
                const realCount = count - extraPaketCounts[role][paket];
                return sum + (anzeigeModus === 'anzahl'
                    ? realCount
                    : realCount * (paketPreise[paket] || 0));
            } else {
                return sum + (anzeigeModus === 'anzahl'
                    ? count
                    : count * (paketPreise[paket] || 0));
            }
        }, 0);
    };

    const totalAll =
        calcSum(paketCounterInstruktoren, 'instruktoren') +
        calcSum(paketCounterMechaniker, 'mechaniker') +
        calcSum(paketCounterGuestmanager, 'guestmanager');


    return (
        <main className="flex flex-col h-full bg-gray-100 overflow-hidden">
            <div className="flex justify-between items-center w-full mx-auto">
                <div className="flex items-center space-x-2 cursor-pointer"
                     onClick={() => navigate(`/leistungspakete?from=${from}&to=${to}`)}>
                    <img src={Arrow} alt="Zurück" className="w-10 h-10"/>
                    <span className="text-4xl font-bold text-gray-900 hover:underline">
                        {eventTypeLabels[eventType] || eventType}
                    </span>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex flex-col -mt-4">
                        <label className="text-sm font-medium text-gray-700">Von</label>
                        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                               className="border border-gray-300 rounded px-2 py-1"/>
                    </div>
                    <div className="flex flex-col -mt-4">
                        <label className="text-sm font-medium text-gray-700">Bis</label>
                        <input type="date" value={to} onChange={e => setTo(e.target.value)}
                               className="border border-gray-300 rounded px-2 py-1"/>
                    </div>
                    <button
                        onClick={() => {
                            fetchEvents();
                            fetchStundenkontingenteExtra();
                            fetchNichtEventStundenkontingente();
                        }}
                        disabled={loading}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-semibold"
                    >
                        {loading ? 'Lädt...' : 'Lade Events'}
                    </button>
                    {eventType !== 'NICHT_EVENT' && (
                        <button
                            onClick={handleExportExcel}
                            className="text-white font-semibold py-2 px-6 rounded flex items-center gap-2"
                            title="Excel Export"
                        >
                            <img src={ExcelExportIcon} alt="Excel icon" className="h-12 w-12"/>
                        </button>
                    )}
                </div>
            </div>


            <div className="w-full mx-auto bg-white rounded-lg p-6 shadow mt-6 mb-6">
                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 items-center">
                    {[
                        {counter: paketCounterInstruktoren, label: 'Instruktoren Pakete', roleKey: 'instruktoren'},
                        {counter: paketCounterMechaniker, label: 'Mechaniker Pakete', roleKey: 'mechaniker'},
                        {counter: paketCounterGuestmanager, label: 'Guestmanager Pakete', roleKey: 'guestmanager'}
                    ]
                        // Remove the filter - always show all three rows
                        .map(({counter, label, roleKey}, index, arr) => {
                            const isLastRow = index === arr.length - 1;
                            const zeilenSumme = calcSum(counter, roleKey);
                            const redPackages = (rotePaketeMap[roleKeyToApiRole[roleKey]] || []).reduce((acc, paket) => {
                                acc[paket] = (acc[paket] || 0) + 1;
                                return acc;
                            }, {});

                            // Calculate red packages sum
                            const redSum = Object.entries(redPackages).reduce((sum, [paket, count]) => {
                                return sum + (anzeigeModus === 'anzahl'
                                    ? count
                                    : count * (paketPreise[paket] || 0));
                            }, 0);

                            const hasRegularPackages = Object.values(counter).some(v => v > 0);
                            const hasRedPackages = Object.values(redPackages).some(v => v > 0);

                            return (
                                <React.Fragment key={label}>
                                    <div
                                        className={`flex items-center pb-2 min-w-0 w-full ${
                                        isLastRow ? '' : 'border-b-2 border-gray-200'
                                    }`}
                                    >
                                        <div className="flex-shrink-0 flex items-center justify-center">
                                            <img src={roleIcons[roleKey]} alt={label} className="h-10 w-10"/>
                                        </div>

                                        {/* Regular packages */}
                                        {/* Scrollable packages area (regular + red) */}
                                        <div className="ml-4 flex-1 min-w-0 w-full overflow-x-auto whitespace-nowrap pr-2 no-scrollbar">
                                            <div className="inline-flex gap-6">
                                                {/* Regular packages */}
                                                <div className="flex">
                                                    {hasRegularPackages ? (
                                                        Object.entries(counter)
                                                            .filter(([, count]) => count > 0)
                                                            .sort(([a], [b]) => {
                                                                const indexA = paketOrder.indexOf(a) === -1 ? paketOrder.length : paketOrder.indexOf(a);
                                                                const indexB = paketOrder.indexOf(b) === -1 ? paketOrder.length : paketOrder.indexOf(b);
                                                                return indexA - indexB;
                                                            })
                                                            .map(([paket, count], i) => (
                                                                <div key={paket + i} className="relative w-28 h-16 flex flex-col items-center justify-center">
                                                                      <span className={`text-lg font-bold ${excludedExtraKeys.includes(paket) ? 'text-blue-600' : 'text-black'}`}>
                                                                        {anzeigeModus === 'anzahl' ? count : `${(count * (paketPreise[paket] || 0))} €`}
                                                                      </span>
                                                                    <span className={`text-sm ${excludedExtraKeys.includes(paket) ? 'text-blue-600' : 'text-gray-500'}`}>
                                                                        {paket}
                                                                      </span>
                                                                </div>
                                                            ))
                                                    ) : (
                                                        <div className="w-28 h-16 flex items-center justify-center text-gray-400 text-sm">Keine Pakete</div>
                                                    )}
                                                </div>

                                                {/* Red packages (excluded names) */}
                                                <div className="flex">
                                                    {hasRedPackages ? (
                                                        Object.entries(redPackages)
                                                            .filter(([, count]) => count > 0)
                                                            .sort(([a], [b]) => {
                                                                const indexA = paketOrder.indexOf(a) === -1 ? paketOrder.length : paketOrder.indexOf(a);
                                                                const indexB = paketOrder.indexOf(b) === -1 ? paketOrder.length : paketOrder.indexOf(b);
                                                                return indexA - indexB;
                                                            })
                                                            .map(([paket, count], i) => (
                                                                <div key={`red-${paket}-${i}`} className="relative w-28 h-16 flex flex-col items-center justify-center">
                                                                      <span className="text-lg font-bold text-red-600">
                                                                        {anzeigeModus === 'anzahl' ? count : `${(count * (paketPreise[paket] || 0))} €`}
                                                                      </span>
                                                                    <span className="text-sm text-red-500">{paket}</span>
                                                                </div>
                                                            ))
                                                    ) : (
                                                        <div className="w-28 h-16 flex items-center justify-center text-gray-400 text-sm">Keine externen Pakete</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    <div className="flex flex-col items-center justify-center">
                                        <span className="text-xl font-bold text-black">
                                            {anzeigeModus === 'anzahl' ? zeilenSumme : `${zeilenSumme} €`}
                                        </span>
                                        <span className="text-sm text-gray-500">{label}</span>
                                        {hasRedPackages && (
                                            <span className="text-sm font-medium text-red-600">
                                                + {anzeigeModus === 'anzahl' ? redSum : `${redSum} €`} (ausgeschlossen)
                                            </span>
                                        )}
                                    </div>

                                    {index === 0 && (
                                        <div
                                            className="row-span-3 flex flex-col items-center justify-center space-y-10">
                                            <div className="flex flex-col items-center">
                                                <span className="text-xl font-bold text-white">
                                                    {anzeigeModus === 'anzahl' ? totalAll : `${totalAll} €`}
                                                </span>
                                                <span className="text-sm text-white">Gesamt</span>
                                            </div>
                                            <div className="flex flex-col items-center">
                                                <span className="text-xl font-bold text-black">
                                                    {anzeigeModus === 'anzahl' ? totalAll : `${totalAll} €`}
                                                </span>
                                                <span className="text-sm text-gray-500">Gesamt</span>
                                            </div>
                                            <div
                                                className="flex items-center bg-gray-200 rounded-full p-1 w-fit shadow-inner">
                                                <button
                                                    onClick={() => setAnzeigeModus('anzahl')}
                                                    className={`w-8 h-8 flex items-center justify-center rounded-full transition ${
                                                        anzeigeModus === 'anzahl' ? 'bg-white shadow text-black' : 'text-gray-500'
                                                    }`}
                                                >
                                                    #
                                                </button>
                                                <button
                                                    onClick={() => setAnzeigeModus('euro')}
                                                    className={`w-8 h-8 flex items-center justify-center rounded-full transition ${
                                                        anzeigeModus === 'euro' ? 'bg-gray-700 text-white shadow-md' : 'text-gray-500'
                                                    }`}
                                                >
                                                    €
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                </div>
            </div>
            <div className="flex-1 w-full overflow-y-auto space-y-3">
                {[...events]
                    .sort((a, b) => dayjs(a.timeframe.startTime).valueOf() - dayjs(b.timeframe.startTime).valueOf())
                    .map(event => (
                        <EventLeistungspaketeCard
                            key={event.id}
                            event={event}
                            onUpdate={fetchEvents}
                            onRotePaketeUpdate={handleRotePaketeUpdate}
                        />

                    ))}
            </div>
        </main>
    )
        ;
};

export default LeistungspaketeStandort;

